/**
 * Меню «⋯» сбоку от сообщения или звонка ленты «Чатов»: действия с объектом одним списком —
 * ответить, переслать в салон, замечание контроля качества (включает выделение: к замечанию можно
 * отметить ещё сообщения). Меню рисуется `fixed` у кнопки — лента с прокруткой его не обрезает;
 * у нижнего края экрана открывается вверх. Закрывают клик мимо, Esc и прокрутка ленты.
 * С клавиатуры: при открытии фокус на первом пункте, стрелки / Home / End — по пунктам, Tab
 * закрывает, Esc закрывает и возвращает фокус на «⋯».
 */
import { MoreHorizontal } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";

export type MenuItem = { label: string; icon: ReactNode; hint: string; onClick: () => void; annot: string };

const MENU_H = 160;

export function MessageMenu({ items, className, children }: { items: MenuItem[]; className: string; children?: ReactNode }) {
  const [at, setAt] = useState<{ x: number; y: number; up: boolean } | null>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!at) return;
    box.current?.querySelector<HTMLElement>("[role=menuitem]")?.focus({ preventScroll: true });
    const close = (e: Event) => {
      if (e.type === "keydown" && (e as KeyboardEvent).key !== "Escape") return;
      if (e.type === "pointerdown" && (box.current?.contains(e.target as Node) || btn.current?.contains(e.target as Node))) return;
      setAt(null);
      if (e.type === "keydown") btn.current?.focus({ preventScroll: true });
    };
    window.addEventListener("pointerdown", close, true);
    window.addEventListener("keydown", close);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("pointerdown", close, true);
      window.removeEventListener("keydown", close);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [at]);
  if (items.length === 0) return null;
  return (
    <>
      <button
        ref={btn}
        type="button"
        onClick={() => {
          if (at) return setAt(null);
          const r = btn.current!.getBoundingClientRect();
          const up = r.bottom + MENU_H > window.innerHeight;
          setAt({ x: Math.min(r.left, window.innerWidth - 232), y: up ? r.top : r.bottom, up });
        }}
        aria-haspopup="menu"
        aria-expanded={!!at}
        title="Действия с сообщением"
        className={className}
        data-annot="меню сообщения"
      >
        {children ?? <MoreHorizontal size={15} />}
      </button>
      {at && (
        <div
          ref={box}
          role="menu"
          onKeyDown={(e) => {
            const list = Array.from(box.current?.querySelectorAll<HTMLElement>("[role=menuitem]") ?? []);
            const i = list.indexOf(document.activeElement as HTMLElement);
            const go = (n: number) => {
              e.preventDefault();
              list[(n + list.length) % list.length]?.focus({ preventScroll: true });
            };
            if (e.key === "ArrowDown") go(i + 1);
            else if (e.key === "ArrowUp") go(i - 1);
            else if (e.key === "Home") go(0);
            else if (e.key === "End") go(list.length - 1);
            else if (e.key === "Tab") setAt(null);
          }}
          className="fixed z-50 w-[224px] overflow-hidden rounded-[12px] bg-[var(--tg-surface)] py-1 text-[14px] text-[var(--tg-text)] shadow-[0_4px_16px_rgba(0,0,0,.2)]"
          style={{ left: Math.max(8, at.x), ...(at.up ? { bottom: window.innerHeight - at.y + 4 } : { top: at.y + 4 }) }}
        >
          {items.map((it) => (
            <button
              key={it.label}
              type="button"
              role="menuitem"
              title={it.hint}
              onClick={() => {
                setAt(null);
                it.onClick();
              }}
              className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-[var(--tg-hover)]"
              data-annot={it.annot}
            >
              <span className="flex-none text-[var(--tg-sec)]">{it.icon}</span>
              {it.label}
            </button>
          ))}
        </div>
      )}
    </>
  );
}
